import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const ExpenseEdit = () => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNo: 0,
        ExpenseHeadId: '',
        Remarks: '',
        Amount: 0,
    });

    useEffect(() => {
        fetchData('ExpenseApi', `/ExpenseData/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData({
                        ...response.response_data,
                        UpdatedBy: userId,
                    });
                }
            })
            .catch((error) => {
                console.error('Error fetching Expense Details:', error);
            });
    }, [id]);

    const handleCustomerSelect = (selectedOption) => {
        setFormData((prevData) => ({
            ...prevData,
            ExpenseHeadId: selectedOption ? selectedOption.value : '',
        }));
    };

    useEffect(() => {
        fetchCustomerData();
    }, []);

    const fetchCustomerData = () => {
        fetchData('ExpenseApi', `/AccountHead/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.ID,
                }));
                setCustomerOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching Account Head data:', error);
            });
    };

    const submitData = async () => {
        setIsLoading(true);
        setErrorMessage('');

        if (!formData.ExpenseHeadId || formData.ExpenseHeadId === 0) {
            setErrorMessage('Account Head is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.Amount || formData.Amount === 0) {
            setErrorMessage('Amount must be greater than 0!');
            setIsLoading(false);
            return;
        }

        const updateFormData = {
            ...formData,
            UpdatedBy: userId,
        };

        updateData('ExpenseApi', `${id}`, updateFormData, token)
            .then(() => {
                setSuccessMessage('Expense updated successfully!');
                history.push('/ExpenseIndex');
            })
            .catch((error) => {
                console.error('Expense Update error:', error);
                setErrorMessage('Failed to update expense.');
                setIsLoading(false);
            });
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <Fragment>
            <div className="form-container">
                <div className="form-card">
                    {/* Header */}
                    <div className="form-header">
                        <h1 className="form-title">Edit Expense</h1>
                        <div className="form-actions">
                            <Link to="/ExpenseIndex" className="btn-back">
                                <ArrowBackIcon fontSize="small" />
                                Back to List
                            </Link>
                            <button className="btn-save" onClick={submitData} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        <SaveIcon fontSize="small" />
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && <div className="message error">{errorMessage}</div>}
                    {successMessage && <div className="message success">{successMessage}</div>}

                    {/* Form Body */}
                    <div className="form-body">
                        {/* Row 1 */}
                        <div className="form-row">
                            <div className="form-group form-select" style={{ flex: 2 }}>
                                <label className="form-label">Account Head *</label>
                                <Select
                                    options={customerOptions}
                                    value={customerOptions.find((option) => option.value === formData.ExpenseHeadId)}
                                    onChange={handleCustomerSelect}
                                    placeholder="Select account head"
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Date *</label>
                                <input
                                    className="form-input"
                                    value={formatDateForInput(formData.BillDate)}
                                    type="date"
                                    name="BillDate"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill No</label>
                                <input
                                    className="form-input"
                                    value={formData.BillNo || ''}
                                    type="text"
                                    name="BillNo"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-input"
                                    value={formData.Remarks || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter remarks"
                                    name="Remarks"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Amount *</label>
                                <input
                                    className="form-input"
                                    value={formData.Amount || ''}
                                    type="number"
                                    placeholder="0.00"
                                    onChange={handleChange}
                                    name="Amount"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ExpenseEdit;
